import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Chip, Autocomplete, CircularProgress,
    Select, MenuItem, FormControl, InputLabel, Grid, Tooltip, TablePagination,
    OutlinedInput, Checkbox, ListItemText
} from '@mui/material';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdClose } from 'react-icons/md';
import { getData, postData, putData, deleteData } from '../../Webservice/Webservice';

const VARIATION_TYPE_OPTIONS = [
    { value: 'size', label: 'Size' },
    { value: 'color', label: 'Color' },
    { value: 'shade', label: 'Shade' },
    { value: 'fragrance', label: 'Fragrance' },
    { value: 'pack_size', label: 'Pack Size' },
    { value: 'flavor', label: 'Flavor' },
    { value: 'formula', label: 'Formula' },
    { value: 'other', label: 'Other' }
];

const VariationGroups = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);

    // Form state
    const [groupName, setGroupName] = useState('');
    const [variationTypes, setVariationTypes] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // Product search for autocomplete
    const [productSearchQuery, setProductSearchQuery] = useState('');
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);

    const commonToken = sessionStorage.getItem('commonToken');
    const sessionToken = sessionStorage.getItem('token');

    const getToken = () => {
        const tokenValue = commonToken || sessionToken;
        return tokenValue ? `Bearer ${tokenValue}` : '';
    };

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const token = getToken();
            const params = {
                page: page + 1,
                limit: rowsPerPage
            };
            if (searchQuery) params.search = searchQuery;

            const response = await getData('variation-group/list', params, token, false);
            if (response && response.success) {
                setGroups(response.groups || []);
                setTotal(response.meta?.total || 0);
            }
        } catch (err) {
            console.error('Error fetching groups:', err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchQuery]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const searchProducts = async (query) => {
        if (!query || query.trim().length < 2) {
            setProductSearchResults([]);
            return;
        }
        setProductSearchLoading(true);
        try {
            const token = getToken();
            const response = await getData(
                'variation-group/search-products',
                { search: query },
                token,
                false
            );
            if (response && response.success) {
                setProductSearchResults(response.products || []);
            }
        } catch (err) {
            console.error('Error searching products:', err);
        } finally {
            setProductSearchLoading(false);
        }
    };

    useEffect(() => {
        const debounce = setTimeout(() => {
            searchProducts(productSearchQuery);
        }, 400);
        return () => clearTimeout(debounce);
    }, [productSearchQuery]);

    const handleOpenCreate = () => {
        setEditingGroup(null);
        setGroupName('');
        setVariationTypes([]);
        setSelectedProducts([]);
        setDialogOpen(true);
    };

    const handleOpenEdit = (group) => {
        setEditingGroup(group);
        setGroupName(group.groupName);
        setVariationTypes(group.variationTypes || []);
        setSelectedProducts(
            (group.products || []).map(p => ({
                product: p.productId,
                attributes: p.attributes instanceof Map
                    ? Object.fromEntries(p.attributes)
                    : (p.attributes || {})
            }))
        );
        setDialogOpen(true);
    };

    const handleAddProduct = (product) => {
        if (!product) return;
        // Check if already added
        if (selectedProducts.some(sp => sp.product._id === product._id)) return;

        const attrs = {};
        variationTypes.forEach(vt => { attrs[vt] = ''; });

        setSelectedProducts(prev => [...prev, { product, attributes: attrs }]);
        setProductSearchQuery('');
        setProductSearchResults([]);
    };

    const handleRemoveProduct = (index) => {
        setSelectedProducts(prev => prev.filter((_, i) => i !== index));
    };

    const handleAttributeChange = (index, attrKey, value) => {
        setSelectedProducts(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                attributes: { ...updated[index].attributes, [attrKey]: value }
            };
            return updated;
        });
    };

    // When variationTypes change, update all product attributes
    useEffect(() => {
        setSelectedProducts(prev =>
            prev.map(sp => {
                const newAttrs = {};
                variationTypes.forEach(vt => {
                    newAttrs[vt] = sp.attributes[vt] || '';
                });
                return { ...sp, attributes: newAttrs };
            })
        );
    }, [variationTypes]);

    const handleSave = async () => {
        if (!groupName.trim()) return alert('Group name is required');
        if (variationTypes.length === 0) return alert('Select at least one variation type');
        if (selectedProducts.length < 2) return alert('Add at least 2 products');

        // Validate all attributes are filled
        for (const sp of selectedProducts) {
            for (const vt of variationTypes) {
                if (!sp.attributes[vt] || !sp.attributes[vt].trim()) {
                    return alert(`Please fill "${vt}" for "${sp.product.title}"`);
                }
            }
        }

        const payload = {
            groupName: groupName.trim(),
            variationTypes,
            products: selectedProducts.map(sp => ({
                productId: sp.product._id,
                attributes: sp.attributes
            }))
        };

        try {
            const token = getToken();
            if (editingGroup) {
                await putData(`variation-group/update/${editingGroup._id}`, payload, token);
            } else {
                await postData('variation-group/create', payload, token);
            }
            setDialogOpen(false);
            fetchGroups();
        } catch (err) {
            console.error('Error saving group:', err);
        }
    };

    const handleDelete = async () => {
        if (!groupToDelete) return;
        try {
            const token = getToken();
            await deleteData(`variation-group/delete/${groupToDelete._id}`, token);
            setDeleteDialogOpen(false);
            setGroupToDelete(null);
            fetchGroups();
        } catch (err) {
            console.error('Error deleting group:', err);
        }
    };

    const getAttributeLabel = (attributes) => {
        if (!attributes) return '';
        const attrs = attributes instanceof Map ? Object.fromEntries(attributes) : attributes;
        return Object.values(attrs).filter(Boolean).join(' / ');
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily: 'Mulish, sans-serif' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    Variation Groups
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<MdAdd />}
                    onClick={handleOpenCreate}
                    sx={{
                        backgroundColor: 'rgb(255, 209, 2)',
                        color: 'black',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontFamily: 'Mulish, sans-serif',
                        '&:hover': { backgroundColor: 'rgb(230, 189, 0)' }
                    }}
                >
                    Create Group
                </Button>
            </Box>

            {/* Search */}
            <Box sx={{ mb: 3, maxWidth: 400 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search groups..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    InputProps={{
                        startAdornment: <MdSearch style={{ marginRight: 8, color: '#999' }} />
                    }}
                />
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif' }}>Group Name</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif' }}>Variation Types</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif' }}>Products</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif' }} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={30} />
                                </TableCell>
                            </TableRow>
                        ) : groups.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5, color: '#999' }}>
                                    No variation groups found
                                </TableCell>
                            </TableRow>
                        ) : (
                            groups.map((group, index) => (
                                <TableRow key={group._id} hover>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell>
                                        <Typography fontWeight={600} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                            {group.groupName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {(group.variationTypes || []).map(vt => (
                                                <Chip
                                                    key={vt}
                                                    label={vt}
                                                    size="small"
                                                    sx={{
                                                        textTransform: 'capitalize',
                                                        fontSize: '0.75rem',
                                                        backgroundColor: '#FFF3CD',
                                                        color: '#856404'
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {(group.products || []).map((p, pi) => (
                                                <Box key={pi} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {p.productId?.product_images?.[0] && (
                                                        <Box
                                                            component="img"
                                                            src={p.productId.product_images[0]}
                                                            sx={{ width: 28, height: 28, borderRadius: 1, objectFit: 'cover' }}
                                                        />
                                                    )}
                                                    <Typography variant="body2" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                                        {p.productId?.title || 'Deleted Product'}
                                                        {' '}
                                                        <Typography component="span" variant="caption" color="text.secondary">
                                                            ({getAttributeLabel(p.attributes)})
                                                        </Typography>
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={group.isActive ? 'Active' : 'Inactive'}
                                            size="small"
                                            sx={{
                                                backgroundColor: group.isActive ? '#D4EDDA' : '#F8D7DA',
                                                color: group.isActive ? '#155724' : '#721C24',
                                                fontWeight: 600
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleOpenEdit(group)}>
                                                <MdEdit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => { setGroupToDelete(group); setDeleteDialogOpen(true); }}
                                            >
                                                <MdDelete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                    }
                }}
            >
                {/* Header */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                    color: '#fff',
                    px: 3,
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <Box>
                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.3px' }}>
                            {editingGroup ? '✏️ Edit Variation Group' : '✨ Create Variation Group'}
                        </Typography>
                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', mt: 0.3 }}>
                            Link products that are variations of each other
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                        <MdClose size={22} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 0 }}>
                    {/* Step 1: Group Info */}
                    <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Box sx={{
                                width: 26, height: 26, borderRadius: '50%',
                                background: 'linear-gradient(135deg, #FFD102 0%, #F5A623 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.75rem', fontWeight: 800, color: '#1a1a2e',
                                fontFamily: 'Mulish, sans-serif',
                            }}>1</Box>
                            <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e' }}>
                                Group Information
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Group Name"
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    placeholder="e.g., H&S Shampoo"
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            '&.Mui-focused fieldset': { borderColor: '#F5A623' }
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': { color: '#F5A623' }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ '&.Mui-focused': { color: '#F5A623' } }}>Variation Types</InputLabel>
                                    <Select
                                        multiple
                                        value={variationTypes}
                                        onChange={(e) => setVariationTypes(e.target.value)}
                                        input={<OutlinedInput label="Variation Types" />}
                                        sx={{
                                            borderRadius: '10px',
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F5A623' }
                                        }}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => (
                                                    <Chip
                                                        key={value}
                                                        label={VARIATION_TYPE_OPTIONS.find(o => o.value === value)?.label || value}
                                                        size="small"
                                                        sx={{
                                                            textTransform: 'capitalize',
                                                            background: 'linear-gradient(135deg, #FFF3CD 0%, #FFE8A0 100%)',
                                                            color: '#856404',
                                                            fontWeight: 600,
                                                            fontSize: '0.72rem',
                                                            border: '1px solid #F5D56E',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    >
                                        {VARIATION_TYPE_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Checkbox
                                                    checked={variationTypes.indexOf(option.value) > -1}
                                                    sx={{ '&.Mui-checked': { color: '#F5A623' } }}
                                                />
                                                <ListItemText
                                                    primary={option.label}
                                                    primaryTypographyProps={{ fontFamily: 'Mulish, sans-serif', fontWeight: 500 }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ mx: 3, borderBottom: '1px dashed #e0e0e0' }} />

                    {/* Step 2: Product Selection */}
                    <Box sx={{ px: 3, pt: 2.5, pb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                    width: 26, height: 26, borderRadius: '50%',
                                    background: variationTypes.length > 0
                                        ? 'linear-gradient(135deg, #FFD102 0%, #F5A623 100%)'
                                        : '#e0e0e0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.75rem', fontWeight: 800,
                                    color: variationTypes.length > 0 ? '#1a1a2e' : '#999',
                                    fontFamily: 'Mulish, sans-serif',
                                    transition: 'all 0.3s',
                                }}>2</Box>
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e' }}>
                                    Add Products
                                </Typography>
                            </Box>
                            {selectedProducts.length > 0 && (
                                <Chip
                                    label={`${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''} added`}
                                    size="small"
                                    sx={{
                                        background: selectedProducts.length >= 2 ? '#D4EDDA' : '#FFF3CD',
                                        color: selectedProducts.length >= 2 ? '#155724' : '#856404',
                                        fontWeight: 600,
                                        fontFamily: 'Mulish, sans-serif',
                                        fontSize: '0.72rem',
                                    }}
                                />
                            )}
                        </Box>

                        {variationTypes.length === 0 ? (
                            <Box sx={{
                                py: 4, textAlign: 'center',
                                borderRadius: '12px', border: '2px dashed #e0e0e0',
                                backgroundColor: '#fafafa',
                            }}>
                                <Typography sx={{ color: '#999', fontFamily: 'Mulish, sans-serif', fontSize: '0.85rem' }}>
                                    👆 Please select variation types first
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Autocomplete
                                    freeSolo
                                    options={productSearchResults.filter(
                                        p => !selectedProducts.some(sp => sp.product._id === p._id)
                                    )}
                                    getOptionLabel={(option) => option.title || ''}
                                    inputValue={productSearchQuery}
                                    onInputChange={(e, value) => setProductSearchQuery(value)}
                                    onChange={(e, value) => handleAddProduct(value)}
                                    loading={productSearchLoading}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} sx={{
                                            display: 'flex', gap: 1.5, alignItems: 'center',
                                            opacity: option.inGroup ? 0.5 : 1,
                                            py: 1.2,
                                        }}>
                                            <Box sx={{
                                                width: 44, height: 44, borderRadius: '8px', overflow: 'hidden',
                                                border: '1px solid #f0f0f0', flexShrink: 0,
                                                backgroundColor: '#fafafa',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {option.product_images?.[0] ? (
                                                    <Box component="img" src={option.product_images[0]}
                                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <Typography sx={{ fontSize: '1.2rem' }}>📦</Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                                    {option.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                                    MRP: ₹{option.b2c_mrp} &nbsp;|&nbsp; SP: ₹{option.b2c_sellingPrice}
                                                    {option.unitValue && ` | ${option.unitValue}${option.unitName || ''}`}
                                                </Typography>
                                                {option.inGroup && (
                                                    <Chip label={`Already in: ${option.inGroup}`} size="small" color="warning"
                                                        sx={{ ml: 1, fontSize: '0.6rem', height: 18 }} />
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="🔍 Search products by name, UPC, or product code..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    '&.Mui-focused fieldset': { borderColor: '#F5A623' }
                                                }
                                            }}
                                        />
                                    )}
                                />

                                {/* Product Cards */}
                                {selectedProducts.length > 0 && (
                                    <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {selectedProducts.map((sp, index) => (
                                            <Paper
                                                key={sp.product._id || index}
                                                elevation={0}
                                                sx={{
                                                    border: '1px solid #e8e8e8',
                                                    borderRadius: '12px',
                                                    p: 2,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        borderColor: '#ccc',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                                                    }
                                                }}
                                            >
                                                {/* Product Info Row */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                    <Box sx={{
                                                        width: 44, height: 44, borderRadius: '8px',
                                                        overflow: 'hidden', flexShrink: 0,
                                                        border: '1px solid #f0f0f0', backgroundColor: '#fafafa',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        {sp.product.product_images?.[0] ? (
                                                            <Box component="img" src={sp.product.product_images[0]}
                                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        ) : (
                                                            <Typography sx={{ fontSize: '1.2rem' }}>📦</Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={700} noWrap sx={{ fontFamily: 'Mulish, sans-serif', color: '#1a1a2e' }}>
                                                            {sp.product.title}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#888', fontFamily: 'Mulish, sans-serif' }}>
                                                            ₹{sp.product.b2c_sellingPrice || sp.product.b2c_mrp || 0}
                                                            {sp.product.b2c_mrp && sp.product.b2c_sellingPrice && sp.product.b2c_mrp > sp.product.b2c_sellingPrice && (
                                                                <span style={{ textDecoration: 'line-through', marginLeft: 6, color: '#bbb' }}>
                                                                    ₹{sp.product.b2c_mrp}
                                                                </span>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    <Tooltip title="Remove product">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveProduct(index)}
                                                            sx={{
                                                                color: '#ccc',
                                                                '&:hover': { color: '#f44336', backgroundColor: '#FFF0F0' }
                                                            }}
                                                        >
                                                            <MdClose size={18} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>

                                                {/* Attribute Inputs Row */}
                                                <Grid container spacing={1.5}>
                                                    {variationTypes.map(vt => (
                                                        <Grid item xs={12} sm={6} md={variationTypes.length === 1 ? 12 : variationTypes.length === 2 ? 6 : 4} key={vt}>
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                label={VARIATION_TYPE_OPTIONS.find(o => o.value === vt)?.label || vt}
                                                                placeholder={`e.g., ${vt === 'size' ? '250ml' : vt === 'color' ? 'Blue' : vt === 'shade' ? 'Natural Beige' : vt === 'fragrance' ? 'Rose' : vt === 'pack_size' ? 'Pack of 3' : vt === 'flavor' ? 'Strawberry' : '...'}`}
                                                                value={sp.attributes[vt] || ''}
                                                                onChange={(e) => handleAttributeChange(index, vt, e.target.value)}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: '8px',
                                                                        backgroundColor: '#FAFAFA',
                                                                        '&.Mui-focused': { backgroundColor: '#fff' },
                                                                        '&.Mui-focused fieldset': { borderColor: '#F5A623' }
                                                                    },
                                                                    '& .MuiInputLabel-root.Mui-focused': { color: '#F5A623' },
                                                                    '& .MuiInputLabel-root': { textTransform: 'capitalize' }
                                                                }}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}

                                {selectedProducts.length === 0 && (
                                    <Box sx={{
                                        mt: 2, py: 3.5, textAlign: 'center',
                                        borderRadius: '12px', border: '2px dashed #e8e8e8',
                                        backgroundColor: '#FAFAFA',
                                    }}>
                                        <Typography sx={{ fontSize: '1.5rem', mb: 0.5 }}>📦</Typography>
                                        <Typography sx={{ color: '#999', fontFamily: 'Mulish, sans-serif', fontSize: '0.85rem' }}>
                                            Search and add products above
                                        </Typography>
                                        <Typography sx={{ color: '#bbb', fontFamily: 'Mulish, sans-serif', fontSize: '0.75rem', mt: 0.3 }}>
                                            Minimum 2 products required
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>

                {/* Footer */}
                <Box sx={{
                    px: 3, py: 2,
                    borderTop: '1px solid #f0f0f0',
                    backgroundColor: '#FAFAFA',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <Typography variant="caption" sx={{ color: '#999', fontFamily: 'Mulish, sans-serif' }}>
                        {selectedProducts.length < 2
                            ? `Add ${2 - selectedProducts.length} more product${2 - selectedProducts.length > 1 ? 's' : ''}`
                            : '✅ Ready to save'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            onClick={() => setDialogOpen(false)}
                            sx={{
                                color: '#666',
                                textTransform: 'none',
                                fontFamily: 'Mulish, sans-serif',
                                fontWeight: 600,
                                borderRadius: '10px',
                                px: 2.5,
                                '&:hover': { backgroundColor: '#f0f0f0' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={selectedProducts.length < 2 || variationTypes.length === 0 || !groupName.trim()}
                            sx={{
                                background: 'linear-gradient(135deg, #FFD102 0%, #F5A623 100%)',
                                color: '#1a1a2e',
                                fontWeight: 700,
                                textTransform: 'none',
                                fontFamily: 'Mulish, sans-serif',
                                borderRadius: '10px',
                                px: 3,
                                boxShadow: '0 4px 12px rgba(245,166,35,0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #F5C800 0%, #E89A1E 100%)',
                                    boxShadow: '0 6px 16px rgba(245,166,35,0.4)',
                                },
                                '&:disabled': {
                                    background: '#e0e0e0',
                                    color: '#999',
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            {editingGroup ? 'Update Group' : 'Create Group'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '12px', minWidth: 350 } }}
            >
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
                    Delete Variation Group?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                        Are you sure you want to delete <strong>"{groupToDelete?.groupName}"</strong>?
                        This will unlink the products but won't delete them.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ color: 'text.secondary', textTransform: 'none', fontFamily: 'Mulish, sans-serif' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        sx={{ textTransform: 'none', fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VariationGroups;
